/**
 * recoveryInsights — read-side translation of an agent run into the four operator questions:
 * what failed, what was preserved, what input/approval is needed, and whether a retry/resume
 * is available.
 *
 * Only fields the run APIs already return are read. Raw runtime material — actions[].request_payload
 * / response_payload, session ids, logs, tool output — is deliberately never touched: the operator
 * gets bounded, human-readable Hebrew instead.
 */
import { degradedAnalysisLabel } from "../components/degradedAnalysisVocabulary";
import { ACTION_TYPE_LABEL as ACTION_TYPE_LABEL_SHARED } from "../components/actionTypeVocabulary";

// Action types as written by the analyst (see services/helpdesk_analyst.py / action_executor.py).
//
// The shared map is the complete key set; the six keys below override it because this surface's claim
// is not the action's *name* but what it did to the ticket ("שליחת תגובה ללקוח", not the generic
// "מסר ללקוח"). Held alone, the six were also the whole key set — so a run whose action this build
// has no bespoke sentence for printed the producer's raw token here. Spreading the shared map closes
// that without changing a word this surface already said.
const ACTION_TYPE_HE = {
  ...ACTION_TYPE_LABEL_SHARED,
  send_customer_message: "שליחת תגובה ללקוח",
  add_comment:           "הוספת הערה פנימית",
  close_ticket:          "סגירת הקריאה",
  escalate:              "העברת הקריאה לגורם אחר",
  reset_password:        "איפוס סיסמה",
  install_software:      "התקנת תוכנה",
};

export function actionTypeLabel(actionType) {
  if (!actionType) return null;
  return ACTION_TYPE_HE[actionType] ?? String(actionType);
}

// ─── Text hygiene ─────────────────────────────────────────────────────────────

// "×ž×¦×‘" — the signature of UTF-8 Hebrew that was decoded as cp1252/Latin-1 and stored that way.
// Some backend failure summaries (manager_agent's crisis and approved-action paths) are persisted
// double-encoded; the same message therefore has to be matched and shown from both encodings.
const MOJIBAKE_RE = /\u00d7[\u0080-\u00ff\u0152\u0153\u0160\u0161\u0178\u017d\u017e\u0192\u02c6\u02dc\u2013\u2014\u2018\u2019\u201a\u201c\u201d\u201e\u2020\u2021\u2022\u2026\u2030\u2039\u203a\u20ac\u2122]/;

// cp1252 typographic code points back to the byte they were decoded from (raw Latin-1 controls
// pass through as-is). Needed because real Hebrew summaries contain an em dash, which the mojibake
// round-trip may have turned into U+2014.
const CP1252_TO_BYTE = {
  0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85, 0x2020: 0x86,
  0x2021: 0x87, 0x02c6: 0x88, 0x2030: 0x89, 0x0160: 0x8a, 0x2039: 0x8b, 0x0152: 0x8c,
  0x017d: 0x8e, 0x2018: 0x91, 0x2019: 0x92, 0x201c: 0x93, 0x201d: 0x94, 0x2022: 0x95,
  0x2013: 0x96, 0x2014: 0x97, 0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b,
  0x0153: 0x9c, 0x017e: 0x9e, 0x0178: 0x9f,
};

export function looksLikeMojibake(text) {
  return typeof text === "string" && MOJIBAKE_RE.test(text);
}

export function recoverMojibake(text) {
  if (typeof text !== "string" || !text) return null;
  let escaped = "";
  for (const ch of text) {
    const code = ch.codePointAt(0);
    const byte = code <= 0xff ? code : CP1252_TO_BYTE[code];
    if (byte == null) return null;
    escaped += `%${byte.toString(16).padStart(2, "0")}`;
  }
  try {
    const decoded = decodeURIComponent(escaped);
    return /[א-ת]/.test(decoded) && !decoded.includes("�") ? decoded : null;
  } catch {
    return null;
  }
}

const TECHNICAL_MARKERS = [
  "traceback",
  "stack trace",
  'file "',
  "exception:",
  "httperror",
  "bearer ",
  "-----begin",
  "<html",
];

// Anything that looks like a credential or an opaque key: never render it, replace it.
const SECRET_PATTERNS = [
  /sk-[A-Za-z0-9_-]{12,}/g,
  /eyJ[A-Za-z0-9_-]{12,}/g,
  /\b[A-Fa-f0-9]{32,}\b/g,
  /\b(?:api[_-]?key|password|secret|access[_-]?token)\b\s*[:=]\s*\S+/gi,
];

/**
 * Bounded human-readable text, or null when the value cannot be shown truthfully and safely:
 * mojibake that does not round-trip, JSON blobs, stack traces / raw payloads, or empty strings.
 */
export function readableText(value, { maxLen = 240 } = {}) {
  if (typeof value !== "string") return null;
  let text = value.trim();
  if (!text) return null;
  if (looksLikeMojibake(text)) {
    const recovered = recoverMojibake(text);
    if (!recovered) return null;
    text = recovered;
  }
  const lowered = text.toLowerCase();
  if (TECHNICAL_MARKERS.some((marker) => lowered.includes(marker))) return null;
  if (/^\s*[[{]/.test(text)) return null;
  let out = text.replace(/\s+/g, " ");
  for (const pattern of SECRET_PATTERNS) out = out.replace(pattern, "[מוסתר]");
  if (out.length > maxLen) out = `${out.slice(0, maxLen - 1).trimEnd()}…`;
  return out;
}

function evidenceText(run) {
  const summary = readableText(run?.result_summary);
  const execMessage = readableText(run?.context?.execution_result?.message);
  return { summary, execMessage };
}

// ─── What failed ──────────────────────────────────────────────────────────────

export const FAILURE_TITLES = {
  crisis_blocked:        "הפעולה שאושרה נחסמה בגלל מצב חירום",
  approval_refused:      "הפעולה נחסמה — אסימון האישור אינו תקף",
  entitlement_blocked:   "הפעולה נחסמה על-ידי מדיניות ההרשאות של הסוכן",
  integration_missing:   "הפעולה דורשת שכבת אינטגרציה שעדיין אינה זמינה",
  ticket_state_changed:  "הפעולה לא בוצעה — מצב הקריאה השתנה מאז האישור",
  ticket_missing:        "הפעולה לא בוצעה — הקריאה לא נמצאה",
  execution_error:       "הפעולה החיצונית נכשלה במהלך הביצוע",
  approved_action_failed:"הפעולה שאושרה לא בוצעה",
  expired_review:        "הריצה פגה — לא אושרה בזמן",
  expired_connection:    "הלקוח לא התחבר בזמן — הריצה בוטלה",
  stuck_analysis:        "הריצה נתקעה בניתוח",
  analysis_error:        "הריצה נכשלה לפני שהניתוח הושלם",
  unknown:               "לא ידוע מה בדיוק נכשל",
};

/** @returns {{key: string, title: string, detail: string|null}} */
export function classifyFailure(run) {
  const exec = run?.context?.execution_result || null;
  const { summary, execMessage } = evidenceText(run);
  const hay = [execMessage, summary].filter(Boolean).join(" \n ");

  const pick = (key) => ({ key, title: FAILURE_TITLES[key], detail: execMessage || summary || null });

  if (hay.includes("מצב חירום") || hay.includes("Crisis Mode")) return pick("crisis_blocked");
  if (hay.includes("אסימון אישור") || hay.includes("אימות אישור")) return pick("approval_refused");
  if (hay.includes("מדיניות הרשאות")) return pick("entitlement_blocked");
  if (hay.includes("שכבת אינטגרציה")) return pick("integration_missing");
  if (hay.includes("הקריאה כבר סגורה") || hay.includes("הקריאה מוזגה")) return pick("ticket_state_changed");
  if (hay.includes("קריאה לא נמצאה")) return pick("ticket_missing");
  if (exec?.executed && !exec.success) return pick("execution_error");
  if (summary?.startsWith("הפעולה שאושרה לא בוצעה")) return pick("approved_action_failed");
  if (exec && !exec.success) return pick("approved_action_failed");
  if (summary?.startsWith("ריצה פגה")) return pick("expired_review");
  if (summary?.startsWith("הלקוח לא התחבר")) return pick("expired_connection");
  if (summary?.startsWith("ריצה תקועה")) return pick("stuck_analysis");
  if (run?.status === "failed") return pick("analysis_error");
  return { key: "unknown", title: FAILURE_TITLES.unknown, detail: null };
}

// ─── What was preserved ───────────────────────────────────────────────────────

/**
 * @returns {Array<{key: string, label: string, tone: "ok"|"warn"|"neutral"}>}
 * Only claims backed by a stored field; when nothing can be verified the caller shows the
 * truthful "לא ידוע" state from an empty list.
 */
export function describePreserved(run) {
  const items = [];
  const analysis = run?.analysis || null;
  if (analysis) {
    // A degraded analysis is not the AI's own work, and its derived confidence (capped at 70 by the
    // mock, so it reads as "medium" rather than "nothing real ran") must be dropped, not shown. Same
    // rule and same label `action_executor` and `manager_agent` already apply to the stored records.
    const degradedLabel = degradedAnalysisLabel(analysis.degraded);
    items.push({
      key: "analysis",
      label: degradedLabel
        ? `${degradedLabel} — הרשומה נשמרה`
        : analysis.confidence != null
          ? `ניתוח ה-AI נשמר (ביטחון ${analysis.confidence}%)`
          : "ניתוח ה-AI נשמר",
      tone: degradedLabel ? "warn" : "ok",
    });
    if (analysis.customer_reply_draft) {
      items.push({ key: "draft", label: "טיוטת תגובה ללקוח נשמרה", tone: "ok" });
    }
    const proposed = actionTypeLabel(analysis.proposed_action?.action_type);
    if (proposed) {
      items.push({ key: "proposed_action", label: `הפעולה המוצעת נשמרה: ${proposed}`, tone: "ok" });
    }
  }

  const exec = run?.context?.execution_result || null;
  // Without the run's context there is no execution record to read, so no claim is made about the
  // customer side either: an operator must not be told "nothing happened" from missing evidence.
  if (run?.context == null) {
    // nothing to claim
  } else if (!exec) {
    items.push({ key: "no_external", label: "לא נרשמה פעולה חיצונית — לא בוצע שינוי מטעם הריצה", tone: "ok" });
  } else if (exec.executed) {
    items.push({
      key: "executed",
      label: `בוצעה פעולה חיצונית לפני הכשל${actionTypeLabel(exec.action_type) ? ` (${actionTypeLabel(exec.action_type)})` : ""}`,
      tone: "warn",
    });
  } else {
    items.push({ key: "blocked_before_exec", label: "הפעולה נחסמה לפני ביצוע — לא חל שינוי אצל הלקוח", tone: "ok" });
  }

  const succeeded = (run?.actions || []).filter((a) => a?.outcome === "succeeded").length;
  if (succeeded > 0) {
    items.push({ key: "actions_done", label: `${succeeded} פעולות הושלמו לפני הכשל`, tone: "neutral" });
  }
  const failedActions = (run?.actions || []).filter((a) => a?.outcome === "failed").length;
  if (failedActions > 0) {
    items.push({ key: "actions_failed", label: `${failedActions} פעולות נכשלו במהלך הריצה`, tone: "warn" });
  }

  return items;
}

// ─── What input / approval is needed ─────────────────────────────────────────

const REVIEW_STATES = new Set(["awaiting_technician_review", "awaiting_approval"]);

export const NEEDED_KINDS = {
  approval:   "approval",
  connection: "connection",
  decision:   "decision",
  waiting:    "waiting",
  none:       "none",
  unknown:    "unknown",
};

export function describeNeeded(run) {
  const status = run?.status;
  const missing = (run?.analysis?.missing_information || [])
    .map((entry) => readableText(typeof entry === "string" ? entry : null, { maxLen: 140 }))
    .filter(Boolean)
    .slice(0, 3);
  // Head this field neutrally, like every other surface does. The mock's `missing_information` is
  // boilerplate about its own state, so "מהניתוח" / "שסומן בניתוח" handed the operator the fallback's
  // own notes as gaps the analysis established — in the block headed "what is needed to proceed".
  // The list itself stays: the disclosure drawn above it (`frp-preserved`) is what makes it honest.
  const missingDetail = missing.length ? `מידע חסר: ${missing.join(" · ")}` : null;

  if (status === "failed") {
    const exec = run?.context?.execution_result || null;
    if (exec?.executed) {
      return {
        kind: NEEDED_KINDS.decision,
        title: "נדרשת החלטה: לבדוק את מה שכבר בוצע אצל הלקוח, ואז ניסיון חוזר או טיפול ידני",
        detail: null,
      };
    }
    return {
      kind: NEEDED_KINDS.decision,
      title: "נדרשת החלטה: ניסיון חוזר או טיפול ידני בקריאה",
      detail: missingDetail,
    };
  }
  if (status === "paused") {
    return {
      kind: NEEDED_KINDS.decision,
      title: run?.analysis
        ? "נדרשת החלטה: להמשיך את הריצה או לעצור אותה סופית"
        : "אין ניתוח שמור להמשיך ממנו — נדרש ניתוח חדש",
      detail: readableText(run?.context?.stop_reason, { maxLen: 160 }),
    };
  }
  if (REVIEW_STATES.has(status)) {
    return {
      kind: NEEDED_KINDS.approval,
      title: "נדרשת סקירה ואישור של טכנאי",
      detail: missingDetail,
    };
  }
  if (status === "waiting_for_customer_connection") {
    return {
      kind: NEEDED_KINDS.connection,
      title: "נדרש חיבור מרחוק ללקוח כדי להמשיך",
      detail: "אם הלקוח לא יתחבר בזמן, הריצה תסתיים אוטומטית.",
    };
  }
  if (status === "waiting_for_customer_answer") {
    return { kind: NEEDED_KINDS.waiting, title: "ממתין לתשובת הלקוח — אין פעולה נדרשת כעת", detail: null };
  }
  if (status === "rejected") {
    return {
      kind: NEEDED_KINDS.decision,
      title: "הריצה נדחתה על-ידי טכנאי — ניתן לפתוח ניתוח חדש מדף הריצה",
      detail: readableText(run?.context?.reject_reason, { maxLen: 160 }),
    };
  }
  return { kind: NEEDED_KINDS.unknown, title: "לא ידוע איזו פעולה נדרשת", detail: null };
}

// ─── Is a retry / resume available ───────────────────────────────────────────

export const RECOVERY_MODES = { retry: "retry", resume: "resume", none: "none" };

/**
 * Mirrors the backend's own gates: POST /retry needs status=failed and agents_run (409/403
 * otherwise), POST /resume needs status=paused AND a stored analysis and agents_run.
 */
export function describeRecoveryAction(run, { canRun = false } = {}) {
  const status = run?.status;
  if (status === "failed") {
    if (!canRun) {
      return { mode: RECOVERY_MODES.retry, available: false, label: null, reason: "אין לך הרשאת הפעלת ריצות (agents_run)" };
    }
    if (run?.ticket_status === "merged") {
      return { mode: RECOVERY_MODES.retry, available: false, label: null, reason: "הקריאה אוחדה לקריאה אחרת — לא ניתן להריץ ניסיון חוזר" };
    }
    return { mode: RECOVERY_MODES.retry, available: true, label: "נסה שוב", reason: null };
  }
  if (status === "paused") {
    if (!canRun) {
      return { mode: RECOVERY_MODES.resume, available: false, label: null, reason: "אין לך הרשאת הפעלת ריצות (agents_run)" };
    }
    if (!run?.analysis) {
      return { mode: RECOVERY_MODES.resume, available: false, label: null, reason: "אין ניתוח שמור להמשיך ממנו — יש לפתוח ניתוח חדש" };
    }
    return { mode: RECOVERY_MODES.resume, available: true, label: "המשך את הריצה", reason: null };
  }
  if (status === "rejected") {
    return { mode: RECOVERY_MODES.none, available: false, label: null, reason: "הריצה נדחתה — ניתן לפתוח ניתוח חדש מדף הריצה" };
  }
  if (REVIEW_STATES.has(status) || status === "waiting_for_customer_connection" || status === "waiting_for_customer_answer") {
    return { mode: RECOVERY_MODES.none, available: false, label: null, reason: "אין ניסיון חוזר — נדרשת החלטה אנושית בדף הריצה" };
  }
  return { mode: RECOVERY_MODES.none, available: false, label: null, reason: "לא ידוע אם ניתן לנסות שוב" };
}

export function buildRecoveryBrief(run, { canRun = false } = {}) {
  return {
    failure: classifyFailure(run),
    preserved: describePreserved(run),
    needed: describeNeeded(run),
    action: describeRecoveryAction(run, { canRun }),
  };
}
